import React, { useState, useEffect } from 'react';
import api from '../utils/ApiManager';
import { theme } from '../utils/theme';

const NotificationConfigEditor = () => {
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    useEffect(() => {
        fetchConfigs();
    }, []);

    const fetchConfigs = async () => {
        try {
            const response = await api.get('/api/admin/notification-configs');
            setConfigs(response.data);
            setLoading(false);
        } catch (err) {
            setError('Failed to load notification configurations: ' + (err.response?.data?.error || err.message));
            setLoading(false);
        }
    };

    const handleToggle = async (id, isEnabled) => {
        try {
            setSuccess(null);
            setError(null);
            await api.put(`/api/admin/notification-configs/${id}`, {
                isEnabled
            });
            const updatedConfigs = configs.map(config =>
                config.id === id ? { ...config, isEnabled } : config
            );
            setConfigs(updatedConfigs);
            setSuccess('Notification setting updated successfully');
        } catch (err) {
            setError('Failed to update notification setting: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleDaysChange = async (id, daysBeforeEvent) => {
        try {
            setSuccess(null);
            setError(null);
            await api.put(`/api/admin/notification-configs/${id}`, {
                daysBeforeEvent: parseInt(daysBeforeEvent)
            });
            const updatedConfigs = configs.map(config =>
                config.id === id ? { ...config, daysBeforeEvent: parseInt(daysBeforeEvent) } : config
            );
            setConfigs(updatedConfigs);
            setSuccess('Days setting updated successfully');
        } catch (err) {
            setError('Failed to update days setting: ' + (err.response?.data?.error || err.message));
        }
    };

    if (loading) {
        return (
            <div className={`${theme.card.base} mb-8`}>
                <div className="animate-pulse flex space-x-4">
                    <div className="flex-1 space-y-4 py-1">
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="space-y-3">
                            <div className="h-4 bg-gray-200 rounded"></div>
                            <div className="h-4 bg-gray-200 rounded"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`${theme.card.base}`}>
            <h2 className="text-xl font-semibold text-[#0C3B2E] mb-6">Notification Settings</h2>

            {error && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
                    <p>{error}</p>
                </div>
            )}

            {success && (
                <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6" role="alert">
                    <p>{success}</p>
                </div>
            )}

            <div className="space-y-6">
                {configs.map(config => (
                    <div key={config.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="flex justify-between items-start mb-2">
                            <div>
                                <h3 className="text-lg font-medium text-gray-900">{config.name}</h3>
                                <p className="text-sm text-gray-500">{config.description}</p>
                            </div>
                            <div className="flex items-center">
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={config.isEnabled}
                                        onChange={(e) => handleToggle(config.id, e.target.checked)}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#6D9773]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#6D9773]"></div>
                                </label>
                            </div>
                        </div>
                        {config.daysBeforeEvent !== null && (
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">
                                    Days before event
                                    <input
                                        type="number"
                                        min="1"
                                        max="30"
                                        value={config.daysBeforeEvent}
                                        onChange={(e) => handleDaysChange(config.id, e.target.value)}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#6D9773] focus:ring-[#6D9773] sm:text-sm"
                                    />
                                </label>
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NotificationConfigEditor;